import React from 'react';
import styled, { css } from 'styled-components';
import {
  typography,
  space,
  color,
  layout,
  border,
  borderRadius,
  position,
} from 'styled-system';
import themeGet from '@styled-system/theme-get';
import shouldForwardProp from '@styled-system/should-forward-prop'

import Box from './Box';

import { customColor } from './utils/getColor';
import isToAs from './utils/isToAs'

const active = css`
  ${customColor('hoverColor')};
  ${customColor('hoverBg', 'backgroundColor')};
  ${customColor('hoverBorder', 'borderColor')};
`;

const ButtonBase = styled.button.withConfig({ shouldForwardProp })`
  padding: 0;
  border: none;
  text-align: center;
  font-family: inherit;
  line-height: 1;
  text-decoration: none;
  ${typography}
  ${layout}
  ${position}
  ${space}
  ${color}
  ${border}
  ${borderRadius}
  appearance: none;
  transition: all ${themeGet('duration', 250)}ms;
  cursor: pointer;
  &:hover,
  &:focus {
    ${props => !props.disabled && active}
  }
  ${props => props.active && !props.disabled && active}
  ${props =>
    props.disabled &&
    `
    cursor: not-allowed;
    opacity: 0.5;
  `}
`;

const InButtonSpan = props => <Box as="span" {...props} />;

const Button = isToAs(({
  leftIcon,
  rightIcon,
  iconSpacing,
  iconSize,
  children,
  verticalAlign,
  ...props
}) => (
  <ButtonBase {...props}>
    {leftIcon ? (
      <Box as={leftIcon} mr={iconSpacing} verticalAlign={verticalAlign} fontSize={iconSize} />
    ) : null}
    <InButtonSpan verticalAlign={verticalAlign}>{children}</InButtonSpan>
    {rightIcon ? (
      <Box as={rightIcon} ml={iconSpacing} verticalAlign={verticalAlign} fontSize={iconSize} />
    ) : null}
  </ButtonBase>
));

Button.defaultProps = {
  border: '1px solid',
  borderColor: 'orange',
  bg: 'orange',
  color: 'white',
  hoverColor: 'lightBlack',
  hoverBg: 'orange',
  hoverBorder: 'orange',
  px: '0.75em',
  py: '0.75em',
  fontSize: '1.25em',
  fontWeight: 'bold',
  iconSpacing: '0.25em',
  borderRadius: '0.25em',
  display: 'inline-block',
  verticalAlign: 'middle',
  iconSize: '1.25em',
  type: 'button',
};

Button.displayName = 'Button';

Button.Purple = props => (
  <Button
    bg="purple"
    border="1px solid"
    borderColor="purple"
    {...props}
  />
);

Button.Danger = props => (
  <Button
    bg="danger"
    border="1px solid"
    borderColor="danger"
    hoverBg="dangerHover"
    hoverBorder="dangerHover"
    hoverColor="white"
    {...props}
  />
);

Button.Secondary = props => (
  <Button
    bg="secondary"
    border="1px solid"
    borderColor="secondary"
    hoverBg="secondaryHover"
    hoverBorder="secondaryHover"
    {...props}
  />
);

Button.Outline = props => (
  <Button
    border="2px solid"
    borderColor="primary"
    bg="transparent"
    color="primary"
    hoverColor="white"
    {...props}
  />
);

Button.Outline.Danger = props => (
  <Button.Danger
    border="2px solid"
    borderColor="danger"
    bg="transparent"
    color="danger"
    hoverColor="white"
    {...props}
  />
);

Button.Transparent = props => (
  <Button
    border="none"
    bg="transparent"
    hoverBg="transparent"
    color="text"
    hoverColor="primary"
    hoverBorder="transparent"
    {...props}
  />
);

export default Button;
